import type { DetailFeature, DetailLayer, MapViewport } from '../types/atlas'

// WHICH GROUND THE SHELL ALREADY HOLDS APPELLATIONS FOR, and what a request for
// more of it should ask.
//
// The appellation layer used to cost a round trip on every camera settle: it
// asked for exactly the frame on screen and remembered one key, the last frame
// it had asked for. Pan a thumb's width and it asked again; pan back and it
// asked a third time. The answers were merged into one held set all along, so
// every one of those requests after the first mostly returned features the
// shell already had.
//
// Two things here stop that, and both are arithmetic on boxes — no map, no
// React, so the layout can import this statically and a test can run it bare:
//
// - a request asks for MORE than the screen (`paddedFrame`), so the ground a
//   reader is about to drag into view has usually arrived already;
// - every answered frame is remembered (`RegionMemory`), and a camera that comes
//   to rest inside remembered ground asks for nothing.

/**
 * How much wider and taller than the screen a request is.
 *
 * 1.5 on each axis, so a quarter of a screen of margin on every side and 2.25
 * times the area. Measured against the real import (276 American
 * appellations), gzipped, for a 1440x900 window:
 *
 *   frame                          on screen     x1.5       x2
 *   Temecula, zoom 11.2               1.1 KB    1.1 KB    1.1 KB    3 features either way
 *   Southern California, zoom 7.9     2.0 KB    3.2 KB    3.9 KB    the coarse tier
 *   Southern California, zoom 8       4.4 KB    6.7 KB    8.5 KB    the same ground, fine
 *   Santa Ynez, zoom 9.5              2.6 KB    3.6 KB    4.1 KB
 *   Napa and Sonoma, zoom 9          13.4 KB   16.8 KB   21.6 KB
 *   Central Coast, zoom 7             6.5 KB    9.1 KB   13.1 KB
 *   California, zoom 6                8.4 KB   13.5 KB   16.2 KB
 *
 * So at 1.5 a frame costs a quarter to a half as much again as the screen
 * alone — including at zoom 8, where the finer tier has just doubled every
 * polygon — and one such frame answers every pan of up to a quarter of a
 * screen in any direction. At 2 it costs up to double for a margin a reader
 * seldom reaches before zooming, and zooming in is a new tier and a new frame
 * whatever the margin was.
 *
 * Only the appellation layer is padded. The winery layer's "showing N of M
 * wineries here" has to keep describing what the reader can see, and a padded
 * winery frame would count ground that is off the screen.
 */
export const REGION_FRAME_PAD = 1.5

// How many answered frames are remembered. Each is six numbers; the bound is
// there so an afternoon of panning does not turn every settle into a walk down
// a list of thousands. Oldest forgotten first — it is also the ground least
// likely to be returned to.
const REMEMBERED_FRAMES = 32

/**
 * A box of ground and how deep it was asked at.
 *
 * `level` is the INTEGER zoom. The server reveals an appellation by comparing
 * its integer `min_zoom` against the request's zoom, so two requests for the
 * same box at 8.2 and 8.9 are the same question, and the memory is kept per
 * level for that reason rather than per camera zoom.
 *
 * `fine` is whether the server answered at its finer tier (`map_zoom.fine`).
 * It is recorded separately because that threshold is the server's to move and
 * need not sit on an integer: if it were 8.5, two frames at level 8 could hold
 * different shapes for the same place.
 */
export interface RegionFrame {
  west: number
  south: number
  east: number
  north: number
  level: number
  fine: boolean
}

export function frameOf(viewport: MapViewport, fineZoom: number): RegionFrame {
  return {
    west: viewport.west,
    south: viewport.south,
    east: viewport.east,
    north: viewport.north,
    level: Math.floor(viewport.zoom),
    fine: viewport.zoom >= fineZoom,
  }
}

/**
 * The frame to ask for when the camera is showing `viewport`: the same centre
 * and zoom, `factor` times as wide and as tall, kept inside the world.
 *
 * In degrees rather than in projected metres. The two differ by the Mercator
 * stretch across one frame, which at the zooms a region is drawn at is a few
 * percent of a margin that is itself an estimate of where a reader will drag.
 */
export function paddedFrame(viewport: MapViewport, factor: number = REGION_FRAME_PAD): MapViewport {
  const lng = ((viewport.east - viewport.west) * (factor - 1)) / 2
  const lat = ((viewport.north - viewport.south) * (factor - 1)) / 2

  return {
    west: Math.max(viewport.west - lng, -180),
    south: Math.max(viewport.south - lat, -90),
    east: Math.min(viewport.east + lng, 180),
    north: Math.min(viewport.north + lat, 90),
    zoom: viewport.zoom,
  }
}

/**
 * Whether everything a camera at `view` would be sent is already in what
 * `held` was sent.
 *
 * Three conditions, and each is the server's own rule read backwards:
 *
 * - the box: the layer answers what intersects a box, so a box inside the held
 *   one intersects nothing the held one did not;
 * - the level: the layer answers `min_zoom <= zoom`, so a frame asked DEEPER
 *   holds every tier a shallower one would — zooming out inside held ground is
 *   free, and zooming in is a new tier and a new request;
 * - the tier: a coarse frame does not cover a camera that is now owed the fine
 *   shapes. The other way round it does, and the merge keeps the finer copy.
 */
export function frameCovers(held: RegionFrame, view: RegionFrame): boolean {
  return (
    held.level >= view.level &&
    (held.fine || !view.fine) &&
    held.west <= view.west &&
    held.south <= view.south &&
    held.east >= view.east &&
    held.north >= view.north
  )
}

/**
 * The ground the shell has been answered for.
 *
 * Only frames that were answered IN FULL are remembered. A frame the server
 * cut short at its feature cap still owes the sub-appellations the cap
 * dropped, so it is drawn and not remembered, and the next settle on it asks
 * again — which is what every settle did before this existed.
 *
 * A level that has been cut short once is also asked for WITHOUT the margin
 * from then on (`crowded`). The cap takes the coarsest tiers first with no
 * regard for where they are, so a padded frame that hits it can lose
 * appellations in the middle of the screen to ones off the edge of it, and the
 * margin is then costing the reader what it was meant to buy. Per level rather
 * than per place: it is a property of how dense the atlas is at that depth, and
 * the cost of being wrong is one frame's margin.
 */
export class RegionMemory {
  private frames: RegionFrame[] = []
  private crowded = new Set<number>()

  covers(view: RegionFrame): boolean {
    return this.frames.some((frame) => frameCovers(frame, view))
  }

  /** An answered frame. Anything it makes redundant is dropped as it goes in. */
  remember(frame: RegionFrame): void {
    this.frames = [frame, ...this.frames.filter((held) => !frameCovers(frame, held))].slice(0, REMEMBERED_FRAMES)
  }

  /**
   * Forgets everything but the newest frame. Called when the merged set had to
   * evict: remembered ground whose features have just been dropped is ground
   * the map would never ask for again and never draw.
   */
  forgetAllBut(frame: RegionFrame | null): void {
    this.frames = frame ? [frame] : []
  }

  crowd(level: number): void {
    this.crowded.add(level)
  }

  isCrowded(level: number): boolean {
    return this.crowded.has(level)
  }

  /** What to ask the server for, for a camera showing `viewport`. */
  frameToAsk(viewport: MapViewport): MapViewport {
    return this.isCrowded(Math.floor(viewport.zoom)) ? viewport : paddedFrame(viewport)
  }
}

export interface MergeResult {
  /** Whether the held set now draws differently: a new place, or a finer shape for a held one. */
  changed: boolean
  /** Whether the cap pushed anything out. */
  evicted: boolean
}

/**
 * Merges one answered frame into the held set, in place.
 *
 * `held` is ordered least-recently-seen first, which is what eviction reads:
 * every feature a frame brings is moved to the end, new or not.
 *
 * A place the shell already holds keeps the object it holds — so the map layer
 * can tell by identity that nothing about it changed — unless the newcomer is
 * the FINER copy. A coarse copy arriving for a place held fine is the ordinary
 * result of zooming out, and replacing the fine shape with it would snap every
 * boundary on screen back to its nine-vertex shard the moment the camera
 * pulled away. Two copies of the same tier are taken to be the same shape.
 */
export function mergeRegionFeatures(
  held: Map<string, DetailFeature>,
  layer: DetailLayer,
  cap: number,
): MergeResult {
  let changed = false
  let evicted = false

  for (const feature of layer.features) {
    const slug = feature.properties.slug
    const current = held.get(slug)
    const keep = current !== undefined && (current.properties.fine === true || feature.properties.fine !== true)

    if (!keep) changed = true

    // Deleted before being set, because a Map keeps a re-set key in its
    // original position — this is what makes iteration order mean "least
    // recently seen first" rather than "first ever seen first".
    held.delete(slug)
    held.set(slug, keep ? current : feature)
  }

  while (held.size > cap) {
    const oldest = held.keys().next().value
    if (oldest === undefined) break

    held.delete(oldest)
    changed = true
    evicted = true
  }

  return { changed, evicted }
}
